import {
  Button,
  DataTable,
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTableSkeleton,
  DebouncedInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  exportTableToCSV,
  useDataTable,
} from '@data-delivery/d3s';
import type { PermissionApi } from '@data-delivery/permission-ui-api';
import type {
  CellContext,
  ColumnDef,
  ColumnFiltersState,
  HeaderContext,
  Row as TableRow,
} from '@tanstack/react-table';
import { EllipsisIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import {
  useBusinessGroupPermission,
  WithBusinessGroupPermission,
} from '../business-group-permission-context';
import { WithPermission } from '../with-permission';

export interface SharedUsersTableProps {
  users: PermissionApi.UserEntity[];
  businessGroupId?: string;
  actionsEnabled?: boolean;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onCreateUser?: () => void;
  onNavigate?: (target: {
    type: 'detail' | 'edit' | 'assignments' | 'ssh-keys' | 'assign-user-assignments';
    userId: string;
  }) => void;
  onResetPassword?: (user: PermissionApi.UserEntity) => void;
  onDeleteUser?: (user: PermissionApi.UserEntity) => void;
  heightClassName?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  globalSearchValue?: string;
  onGlobalSearchChange?: (value: string) => void;
  onResetGrid?: () => void;
  enableExport?: boolean;
  disableToolbar?: boolean;
  enableAssignmentsFacet?: boolean;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  userRoles?: PermissionApi.BusinessGroupRoleEntity[];
  resourceType?: 'users' | 'configUsers';
}

export function SharedUsersTable({
  users,
  businessGroupId,
  actionsEnabled,
  isLoading,
  isError,
  error,
  onCreateUser,
  onNavigate,
  onResetPassword,
  onDeleteUser,
  heightClassName = 'h-[calc(100vh-250px)]',
  emptyTitle = 'Users',
  emptyDescription = 'No users found',
  globalSearchValue,
  onGlobalSearchChange,
  onResetGrid,
  enableExport,
  disableToolbar,
  enableAssignmentsFacet,
  columnFilters,
  onColumnFiltersChange,
  userRoles = [],
  resourceType = 'users',
}: SharedUsersTableProps) {
  const rows = users;
  type UserRow = (typeof rows)[number];
  const bgPermissionContext = useBusinessGroupPermission();

  const columns = useMemo<Array<ColumnDef<UserRow>>>(
    () => [
      {
        id: 'actions',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Actions' />,
        cell: ({ row }) => (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' aria-label='Actions'>
                <EllipsisIcon className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start'>
              <DropdownMenuItem
                onClick={() =>
                  (actionsEnabled || businessGroupId) &&
                  onNavigate?.({ type: 'detail', userId: row.original.id })
                }
              >
                Detail
              </DropdownMenuItem>
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource={resourceType} action='update'>
                  <DropdownMenuItem
                    onClick={() =>
                      (actionsEnabled || businessGroupId) &&
                      onNavigate?.({ type: 'edit', userId: row.original.id })
                    }
                  >
                    Edit
                  </DropdownMenuItem>
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource={resourceType} action='update'>
                  <DropdownMenuItem
                    onClick={() =>
                      (actionsEnabled || businessGroupId) &&
                      onNavigate?.({ type: 'edit', userId: row.original.id })
                    }
                  >
                    Edit
                  </DropdownMenuItem>
                </WithPermission>
              )}
              <DropdownMenuItem
                onClick={() =>
                  (actionsEnabled || businessGroupId) &&
                  onNavigate?.({ type: 'assignments', userId: row.original.id })
                }
              >
                Assignments
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() =>
                  (actionsEnabled || businessGroupId) &&
                  onNavigate?.({ type: 'ssh-keys', userId: row.original.id })
                }
              >
                SSH Keys
              </DropdownMenuItem>
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource={resourceType} action='update'>
                  <DropdownMenuItem
                    onClick={() =>
                      (actionsEnabled || businessGroupId) &&
                      onNavigate?.({ type: 'assign-user-assignments', userId: row.original.id })
                    }
                  >
                    Assign Assignments
                  </DropdownMenuItem>
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource={resourceType} action='update'>
                  <DropdownMenuItem
                    onClick={() =>
                      (actionsEnabled || businessGroupId) &&
                      onNavigate?.({ type: 'assign-user-assignments', userId: row.original.id })
                    }
                  >
                    Assign Assignments
                  </DropdownMenuItem>
                </WithPermission>
              )}
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource={resourceType} action='update'>
                  <DropdownMenuItem onClick={() => onResetPassword?.(row.original)}>
                    Reset Password
                  </DropdownMenuItem>
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource={resourceType} action='update'>
                  <DropdownMenuItem onClick={() => onResetPassword?.(row.original)}>
                    Reset Password
                  </DropdownMenuItem>
                </WithPermission>
              )}
              {bgPermissionContext ? (
                <WithBusinessGroupPermission resource={resourceType} action='delete'>
                  <DropdownMenuItem
                    onClick={() => onDeleteUser?.(row.original)}
                    className='text-red-600'
                  >
                    Delete User
                  </DropdownMenuItem>
                </WithBusinessGroupPermission>
              ) : (
                <WithPermission roles={userRoles} resource={resourceType} action='delete'>
                  <DropdownMenuItem
                    onClick={() => onDeleteUser?.(row.original)}
                    className='text-red-600'
                  >
                    Delete User
                  </DropdownMenuItem>
                </WithPermission>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableSorting: false,
        size: 48,
      },

      ...(enableAssignmentsFacet
        ? [
            {
              id: 'hasAssignments',
              accessorFn: (r: UserRow) =>
                r.assignments && r.assignments.length > 0 ? 'with' : 'without',
              header: (ctx: HeaderContext<UserRow, unknown>) => (
                <DataTableColumnHeader column={ctx.column} title='Assignments' />
              ),
              cell: (ctx: CellContext<UserRow, unknown>) => (
                <span>{ctx.row.original.assignments?.length ? 'Yes' : 'No'}</span>
              ),
              filterFn(row: TableRow<UserRow>, columnId: string, filterValues: string[]) {
                if (!filterValues || filterValues.length === 0) {
                  return true;
                }

                const value = String(row.getValue(columnId));
                return filterValues.includes(value);
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: 'loginId',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Login ID' />,
        cell: ({ row, getValue }) => (
          <Button
            variant='link'
            className='p-0 m-0'
            onClick={() =>
              (actionsEnabled || businessGroupId) &&
              onNavigate?.({ type: 'detail', userId: row.original.id })
            }
          >
            {getValue<string>()}
          </Button>
        ),
      },
      {
        id: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
        accessorFn: r => `${(r.firstName || '').trim()} ${(r.lastName || '').trim()}`.trim(),
        cell: ({ getValue }) => <span>{(getValue<string>() || '—').trim() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'isSystemUser',
        header: ({ column }) => <DataTableColumnHeader column={column} title='System User' />,
        cell: ({ getValue }) => <span>{getValue<boolean>() ? 'Yes' : 'No'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'orgName',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Org Name' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'rootFolder',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Root Folder' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'idmId',
        header: ({ column }) => <DataTableColumnHeader column={column} title='UMS ID' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'serviceAccountOwner',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Service Account Owner' />
        ),
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        id: 'lastModified',
        accessorFn: r => r.lastModified ?? 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title='Last Modified' />,
        cell: ({ row }) => (
          <span>
            {row.original.lastModified ? new Date(row.original.lastModified).toLocaleString() : '—'}
          </span>
        ),
        sortingFn: 'basic',
        enableSorting: true,
      },
    ],
    [
      enableAssignmentsFacet,
      bgPermissionContext,
      resourceType,
      userRoles,
      actionsEnabled,
      businessGroupId,
      onNavigate,
      onResetPassword,
      onDeleteUser,
    ],
  );

  const table = useDataTable<UserRow>({
    data: rows,
    columns,
    getRowId: r => r.id,
    initialState: {
      sorting: [{ id: 'lastModified', desc: true }],
    },
    externalState: {
      ...(onGlobalSearchChange && globalSearchValue !== undefined
        ? {
            globalFilter: {
              state: globalSearchValue || '',
              setState: onGlobalSearchChange,
            },
          }
        : {}),
      ...(onColumnFiltersChange && columnFilters !== undefined
        ? {
            filters: {
              state: columnFilters || [],
              setState: onColumnFiltersChange,
            },
          }
        : {}),
    },
  });

  const handleGlobalSearchChange = useCallback(
    (e: string | number) => {
      const next = e.toString();
      if (next === (globalSearchValue || '')) {
        return;
      }

      onGlobalSearchChange?.(next);
    },
    [globalSearchValue, onGlobalSearchChange],
  );

  return (
    <div className='flex flex-col gap-4'>
      {!disableToolbar && (
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between gap-2'>
            {onGlobalSearchChange !== undefined && (
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <DebouncedInput
                  type='text'
                  value={globalSearchValue || ''}
                  onChange={handleGlobalSearchChange}
                  placeholder='Search users…'
                  className='h-9 flex-1 w-full min-w-0 rounded-md border bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                />
                {enableAssignmentsFacet && (
                  <div className='flex items-center'>
                    {/* Faceted filter for assignments presence */}
                    <DataTableFacetedFilter
                      column={table.getColumn('hasAssignments')}
                      title='Assignments'
                      options={[
                        { label: 'With Assignments', value: 'with' },
                        { label: 'Without Assignments', value: 'without' },
                      ]}
                    />
                  </div>
                )}
              </div>
            )}
            <div className='flex items-center gap-2'>
              {onCreateUser &&
                (actionsEnabled || businessGroupId) &&
                (bgPermissionContext ? (
                  <WithBusinessGroupPermission resource={resourceType} action='create'>
                    <Button
                      onClick={() => {
                        onCreateUser?.();
                      }}
                    >
                      Create User
                    </Button>
                  </WithBusinessGroupPermission>
                ) : (
                  <WithPermission roles={userRoles} resource={resourceType} action='create'>
                    <Button
                      onClick={() => {
                        onCreateUser?.();
                      }}
                    >
                      Create User
                    </Button>
                  </WithPermission>
                ))}
              {onResetGrid && (
                <Button
                  variant='outline'
                  onClick={() => {
                    onResetGrid();
                  }}
                >
                  Reset Grid
                </Button>
              )}
              {enableExport && (
                <Button
                  variant='secondary'
                  onClick={() => {
                    try {
                      exportTableToCSV(table, {
                        filename: 'users',
                        excludeColumns: ['actions'],
                        customHeaders: {
                          loginId: 'Login ID',
                          isSystemUser: 'System User',
                          serviceAccountOwner: 'Service Account Owner',
                          orgName: 'Org Name',
                          rootFolder: 'Root Folder',
                          idmId: 'UMS ID',
                          lastModified: 'Last Modified',
                        },
                      });
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error('Export failed', err);
                    }
                  }}
                >
                  Export
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <DataTableSkeleton
          withPagination={false}
          rowCount={10}
          columnCount={columns.length}
          searchableColumnCount={0}
          filterableColumnCount={0}
          showViewOptions={false}
          cellWidths={['3rem', '14rem', '16rem', '8rem', '10rem', '10rem', '12rem', '12rem']}
          shrinkZero
        />
      ) : isError ? (
        <div className='text-destructive text-sm'>
          Failed to load users:{' '}
          {(() => {
            const err = error;
            if (err && typeof err === 'object' && 'message' in err) {
              const msg = (err as { message?: string }).message;
              return msg || 'Unknown error';
            }

            return 'Unknown error';
          })()}
        </div>
      ) : (
        <div className='flex flex-col rounded-md border'>
          <DataTable
            table={table}
            className={heightClassName}
            emptyPlaceholderTitle={emptyTitle}
            emptyPlaceholderDescription={emptyDescription}
          />
        </div>
      )}
    </div>
  );
}

export default SharedUsersTable;
